/**
 * tp-delivery-data.js — Single source of truth for FSA → delivery coverage.
 *
 * Refactored from tp-delivery-checker.js. Data source:
 *   docs/research/fleetnow-delivery-coverage.json  (embedded snapshot below)
 * plus the base shipping-fee schedule from M2 spec §8.1 (owner config, NOT in the JSON).
 *
 * Consumed by tp-delivery-stepper.js (cart drawer + /cart) and the legacy postal checker.
 * Plain browser global (no ES modules) to match the theme's `<script defer>` assets.
 *
 * Exposes window.TPDeliveryData:
 *   normalizePostalCode(value) -> 'M5V'            (first 3 chars, upper)
 *   lookupFSA(fsa)             -> coverage object  (see shape at bottom of lookupFSA)
 *   computeBaseFee(feeZone, subtotalCents)         (§8.1 preview; final fee is native checkout)
 *   FEE_PRODUCT_VARIANTS, WINDOWS, PICKUP_WINDOWS, CUTOFFS, NO_SAMEDAY_DOW
 */
(function () {
  if (window.TPDeliveryData) return;

  // ── Owner config: midday surcharge fee-product variant IDs (§8.2) ───────────
  // Created by owner via tools/create-fee-products.sh. Fill the two GIDs here.
  // Until filled, the stepper treats midday surcharge as unavailable (fails safe:
  // it will fall back to evening-only for plus_5/plus_8 rather than add a bad line).
  // Delivery Time Surcharge (Product 9170304237706), created by owner 2026-07-06.
  // ⚠️ 2026-07-19: fee-product variants were unintentionally rebuilt by the cutover
  // 全量写入 (regenerated IDs); restored to correct structure/prices and IDs re-pointed here.
  var FEE_PRODUCT_VARIANTS = {
    plus_5: '49756347990154', // "Midday window (+$9.95)" (restored 2026-07-19; was 49682533810314)
    plus_8: '49756348022922', // "Midday window (+$14.95)" (restored 2026-07-19; was 49682533843082)
    // Door-to-Door Service (Deliver to Unit) — Product 9175255384202, created 2026-07-08 (owner).
    // Optional premium service, $9.95, ALL delivery zones: driver brings the order up to the unit
    // (condo/apartment). Standard delivery = building entrance.
    door: '49756348383370', // (restored 2026-07-19; was 49690489979018)
    // Raised-price tier for door-to-door, $12.95 (owner 2026-09-03). Used ONLY when the cart would
    // otherwise have been charged the base delivery fee but this line tips it over the zone's
    // free-delivery threshold — Shopify's Local Delivery counts every cart line toward that
    // threshold (verified live 2026-09-03; requiresShipping=false does NOT exempt it), so without
    // this the store nets −$3.04 on those orders. Separate same-titled product, not a second
    // variant: a multi-variant product would print the variant name in the cart and expose the rule.
    // Setting this to null is the fail-safe → always the base price. Band logic lives in tp-delivery-stepper.js.
    door_high: '50124536971402', // Product 9282053308554, $12.95, taxable (HST), created 2026-09-03
    // Plan A (owner 2026-07-07): NO base-delivery compensation. The rare narrow-band under-collection
    // (midday + a checkout discount that drops the cart back under $85) is accepted — it's only ever a
    // small leak, never a double-charge, and only when midday is chosen. Native Local Delivery collects
    // the base fee normally. The compensation machinery below stays dormant; re-enable by restoring the
    // "Delivery Fee" $4.95 variant ID (49756333408394, Product 9171010584714; restored 2026-07-19) here.
    base_delivery: null,
  };

  // ── Window labels (customer-facing English) ─────────────────────────────────
  // Evening label authority = owner decision 2026-07-03 (spec §10 #8): "5:30–10:30 PM";
  // 2026-10-08 owner (TP-SYNC-20261006-MOBILE-UX-23 merge): evening window was set to "5:30–11:30 PM" site-wide.
  // 2026-10-12 owner: back to "5:30–10:30 PM" = courier FleetNow 17:30–22:00 + 30 min buffer (all 161 daily FSAs
  // checked on FleetNow's quote tool, none later than 22:00). The "some areas until 11:30" note was dropped.
  // Fixed-weekday zones: FleetNow now quotes 18:55–23:30 → owner set "6:45–11:30 PM"; Stouffville (L4A) is
  // quoted 17:30–23:00 → owner: write the courier's own window ("5:30–11:00 PM"), see SPECIAL_WINDOW_BY_FSA.
  var WINDOWS = {
    evening: { id: 'evening', label: '5:30–10:30 PM', kind: 'evening' },
    midday: { id: 'midday', label: '1:30–5:00 PM', kind: 'midday' }, // next-day+ only (§8.4)
    special: { id: 'special', label: '6:45–11:30 PM', kind: 'special' }, // fixed-weekday zones (FleetNow 18:55–23:30)
    special_stouffville: { id: 'special', label: '5:30–11:00 PM', kind: 'special' }, // L4A only (FleetNow 17:30–23:00)
  };

  // Evening labels already written into live carts (tp_window attribute) before a label change.
  // Mapped to the current label on load so an open cart keeps its pick instead of losing it.
  // A special-zone cart in L4A maps to the zone label first; the stepper then sees it is not offered and asks again.
  var LEGACY_WINDOW_LABELS = { '5:30–11:30 PM': WINDOWS.evening.label, '6:30–11:00 PM': WINDOWS.special.label };
  function normalizeWindowLabel(label) {
    return (label && LEGACY_WINDOW_LABELS[label]) || label || null;
  }

  // §8.5 store pickup windows (fixed, unrelated to delivery zones/surcharge).
  // Keyed by JS getDay(): 0=Sun … 6=Sat.
  var PICKUP_WINDOWS = {
    weekday: [ // Mon–Sat
      { id: 'pickup_1400', label: '2:00–3:00 PM', start: '14:00' },
      { id: 'pickup_1500', label: '3:00–4:00 PM', start: '15:00' },
      { id: 'pickup_1600', label: '4:00–5:00 PM', start: '16:00' },
      { id: 'pickup_1700', label: '5:00–6:00 PM', start: '17:00' },
    ],
    sunday: [ { id: 'pickup_1300', label: '1:00–2:00 PM', start: '13:00' } ],
  };

  // Same-day pickup readiness (owner rule 2026-07-23): an order placed today needs prep time,
  // quantised to the order's clock hour — earliest same-day slot start = max(floorHour, order
  // hour + prepHours). e.g. 8 AM → 3–4 PM (floor), 2:xx PM → 4–5 PM, 3:xx PM → 5–6 PM;
  // the 4:00 PM same-day cut-off (CUTOFFS.pickup) is unchanged. Past the 17:00 next-day
  // rollover (CUTOFFS.middayNextDay) TOMORROW uses the same-day floor (3–4 PM earliest —
  // overnight covers prep, no hour shift), so the 2:00–3:00 PM slot is not offered then.
  var PICKUP_SAMEDAY = { floorHour: 15, prepHours: 2 };

  // §8.4 same-day cut-offs (local store time, 24h).
  // middayNextDay = the 5:00 PM "next-day rollover" (owner rules 2026-07-20 + 2026-07-23):
  // past 17:00 TOMORROW is treated like today — delivery loses the midday window (evening
  // only) and pickup starts at the same-day floor (see PICKUP_SAMEDAY). Dates from the day
  // after tomorrow onward are unaffected.
  var CUTOFFS = { delivery: '14:30', pickup: '16:00', middayNextDay: '17:00' };

  // Days with NO same-day service at all (delivery + pickup). Keyed by JS getDay():
  // 0=Sun … 6=Sat. Owner order 2026-07-19: no express/same-day runs on Sundays —
  // Sunday itself stays bookable as an advance/next-day date, only "today" is blocked.
  var NO_SAMEDAY_DOW = [0];

  // ── FSA coverage snapshot (verbatim from fleetnow-delivery-coverage.json) ────
  var EVENING = ['L0J','L3L','L3P','L3R','L3S','L3T','L3X','L4B','L4C','L4E','L4G','L4H','L4J','L4K','L4L','L4S','L4T','L4V','L4W','L4X','L4Y','L4Z','L5A','L5B','L5C','L5E','L5G','L5H','L5J','L5K','L5L','L5M','L5N','L5P','L5R','L5S','L5T','L5V','L5W','L6A','L6B','L6C','L6E','L6G','L6H','L6J','L6K','L6L','L6M','L6P','L6R','L6S','L6T','L6V','L6W','L6X','L6Y','L6Z','L7A','L7B','M1B','M1C','M1E','M1G','M1H','M1J','M1K','M1L','M1M','M1N','M1P','M1R','M1S','M1T','M1V','M1W','M1X','M2H','M2J','M2K','M2L','M2M','M2N','M2P','M2R','M3A','M3B','M3C','M3H','M3J','M3K','M3L','M3M','M3N','M4A','M4B','M4C','M4E','M4G','M4H','M4J','M4K','M4L','M4M','M4N','M4P','M4R','M4S','M4T','M4V','M4W','M4X','M4Y','M5A','M5B','M5C','M5E','M5G','M5H','M5J','M5K','M5M','M5N','M5P','M5R','M5S','M5T','M5V','M6A','M6B','M6C','M6E','M6G','M6H','M6J','M6K','M6L','M6M','M6N','M6P','M6R','M6S','M7A','M8V','M8W','M8X','M8Y','M8Z','M9A','M9B','M9C','M9L','M9M','M9N','M9P','M9R','M9V','M9W'];
  var MIDDAY_FREE = ['M5B','M2M','M5C','M1W','L4S','M2P','M3H','L3S','M4G','M5L','M1C','M4M','L6G','M4Y','M5M','L6E','M5J','M5R','M2K','M1V','M5K','M3C','M5N','M4J','L4C','M5V','M2N','M2R','M1N','M1E','M1P','M4C','M1J','M1T','M1M','M5A','M1K','M4L','M2L','M1S','M2J','M2H','M1G','L3R','L3P','M5P','L3T','M5G','M4A','M4S','M1H','M5S','M4V','M3A','L4B','M1L','M4W','L4J','M5T','M1B','M4H','M4K','M7A','M5E','M4R','M4X','M4B','M4E','M5H','M4P','L6C','M4N','M4T','M5X','L6B','M1R','M1X','M3B'];
  var MIDDAY_PLUS5 = ['M9W','M6K','M3M','M9V','M8Y','M9B','M9R','M8Z','M6R','M6A','M6L','M6M','M9P','M6B','M6E','M6N','M6P','M3K','M3N','M8X','M5P','M6S','M6H','M6G','M6J','M9L','M9N','M3L','M8V','M9C','M9A','M3J','M9M','M8W','M6C'];
  var MIDDAY_PLUS8 = ['L5J','L6A','L5W','L5R','L5C','L5A','L4V','L4H','L4W','L5E','L5N','L4T','L4Y','L4Z','L4X','L5L','L4K','L5G','L5H','L5V','L0J','L5S','L3L','L5K','L5B','L5T','L4L','L5P','L5M'];
  // not_available = in EVENING but no midday tier at any price (computed as fallback).

  // Zone names verbatim from the canonical reference (docs/delivery-and-pickup-reference.md).
  var SPECIAL_ZONES = [
    { name: 'Hamilton / Burlington', feeZone: 'Z3', days: ['Tue','Thu','Sun'],
      fsas: ['L7L','L7N','L7R','L7S','L7T','L8H','L8K','L8L','L8M','L8P','L8R','L8S','L8T','L8V','L8W','L9A','L9B','L9C','L9H','L9K'] },
    { name: 'Waterloo / Kitchener / Guelph', feeZone: 'Z2', days: ['Wed','Sat'],
      fsas: ['N1E','N1G','N1H','N1K','N1L','N2A','N2B','N2C','N2E','N2G','N2H','N2J','N2K','N2L','N2M','N2N','N2P','N2R','N2T','N2V'] },
    { name: 'Stouffville / Durham', feeZone: 'Z4', days: ['Mon','Fri'],
      fsas: ['L1G','L1J','L1K','L1N','L1P','L1R','L1S','L1T','L1V','L1W','L1X','L1Z','L4A'] },
  ];

  // §8.1 Z5 Brampton FSA set — 🔶 no clean source in the JSON; carried from legacy
  // tp-delivery-checker.js bramptonPrefixes. Owner may correct this one constant.
  var BRAMPTON_Z5 = ['L6P','L6R','L6S','L6T','L6V','L6W','L6X','L6Y','L6Z','L7A'];

  // Generic FSA-prefix → city label (region_map_by_fsa_prefix in the JSON).
  var REGION_BY_PREFIX = {
    L0:'King City / Nobleton', L1:'Durham (Oshawa/Whitby/Ajax/Pickering)',
    L3:'Markham / Richmond Hill / Aurora', L4:'Vaughan / Markham / Richmond Hill',
    L5:'Mississauga', L6:'Mississauga / Brampton / Oakville', L7:'Oakville / Milton / Burlington',
    L8:'Hamilton', L9:'Hamilton / Burlington / Caledon', M1:'Scarborough', M2:'North York',
    M3:'North York', M4:'East Toronto / East York', M5:'Downtown Toronto', M6:'West Toronto',
    M7:"Queen's Park", M8:'Etobicoke', M9:'Etobicoke', N1:'Guelph', N2:'Kitchener / Waterloo',
  };

  // ── §8.1 base shipping-fee schedule (owner config; preview only) ─────────────
  // fee/thresholds in cents. minOrder = min subtotal to deliver at all (below → not offered).
  var FEE_ZONES = {
    Z1: { minOrder: 0,    tiers: [ { under: 3000, fee: 1242 }, { under: 8500, fee: 559 } ], freeAt: 8500 },
    Z2: { minOrder: 3000, tiers: [ { under: 12500, fee: 1695 } ],                            freeAt: 12500 },
    Z3: { minOrder: 3000, tiers: [ { under: 12500, fee: 1356 } ],                            freeAt: 12500 },
    Z4: { minOrder: 3000, tiers: [ { under: 10000, fee: 1130 } ],                            freeAt: 10000 },
    Z5: { minOrder: 3000, tiers: [ { under: 8500, fee: 785 } ],                              freeAt: 8500 },
  };

  // ── Normalize the known JSON anomalies (§8.0) into deterministic sets ────────
  function toSet(arr) { var s = {}; for (var i = 0; i < arr.length; i++) s[arr[i]] = true; return s; }
  var eveningSet = toSet(EVENING);
  var freeSet = toSet(MIDDAY_FREE);
  var plus5Set = toSet(MIDDAY_PLUS5);
  var plus8Set = toSet(MIDDAY_PLUS8);
  // M5P appears in both free and plus_5 → favour the customer: keep free.
  // M6J: removed from MIDDAY_FREE 2026-10-01 (owner: courier raised the rate) → now plus_5 like M6G/M6H.
  delete plus5Set['M5P'];
  // M5L, M5X in free midday but missing from the evening list → treat as also evening.
  eveningSet['M5L'] = true; eveningSet['M5X'] = true;

  // fsa → special zone (first match wins).
  var specialByFsa = {};
  SPECIAL_ZONES.forEach(function (z) { z.fsas.forEach(function (f) { if (!specialByFsa[f]) specialByFsa[f] = z; }); });
  var bramptonSet = toSet(BRAMPTON_Z5);

  // Resolved pending FSAs — align this embedded snapshot with fleetnow JSON (commit f0fc952):
  //   L3Y        → Z1 core, evening only (midday tier TBD → treated as no-midday)
  //   L7M/L8N/L9G→ Z3 Burlington & Hamilton special zone (Tue/Thu/Sun; FleetNow quotes 18:55–23:30 for them, 2026-10-12)
  //   N0B        → dropped (already absent from this snapshot)
  eveningSet['L3Y'] = true;
  ['L7M', 'L8N', 'L9G'].forEach(function (f) { specialByFsa[f] = SPECIAL_ZONES[0]; });

  // Per-FSA window inside a fixed-weekday zone (default = WINDOWS.special).
  var SPECIAL_WINDOW_BY_FSA = { L4A: WINDOWS.special_stouffville };

  function normalizePostalCode(value) {
    return (value || '').replace(/[^a-z0-9]/gi, '').toUpperCase().slice(0, 3);
  }

  function middayTierOf(fsa) {
    if (freeSet[fsa]) return 'free';
    if (plus5Set[fsa]) return 'plus_5';
    if (plus8Set[fsa]) return 'plus_8';
    return 'not_available';
  }

  function surchargeFor(tier) {
    if (tier === 'plus_5') return { charge: 9.95, variant: FEE_PRODUCT_VARIANTS.plus_5 };
    if (tier === 'plus_8') return { charge: 14.95, variant: FEE_PRODUCT_VARIANTS.plus_8 };
    return { charge: 0, variant: null };
  }

  function regionLabel(fsa) { return REGION_BY_PREFIX[fsa.slice(0, 2)] || 'the GTA'; }

  /**
   * lookupFSA('M5V') →
   *   { fsa, deliverable, kind:'daily'|'special'|null, zoneName, feeZone,
   *     days:[...] | null (null = every day), windows:[{id,label,kind}],
   *     middayTier, surchargeCharge, surchargeVariant }
   */
  function lookupFSA(input) {
    var fsa = normalizePostalCode(input);
    if (fsa.length < 3) return { fsa: fsa, deliverable: false, kind: null, reason: 'incomplete' };

    // Special (fixed-weekday) zones take priority (§8.0 resolve order).
    var sp = specialByFsa[fsa];
    if (sp) {
      return {
        fsa: fsa, deliverable: true, kind: 'special', zoneName: sp.name, feeZone: sp.feeZone,
        days: sp.days.slice(), windows: [SPECIAL_WINDOW_BY_FSA[fsa] || WINDOWS.special],
        middayTier: 'not_available', surchargeCharge: 0, surchargeVariant: null,
      };
    }

    // Daily core (evening ± midday).
    if (eveningSet[fsa]) {
      var tier = middayTierOf(fsa);
      var s = surchargeFor(tier);
      // Only offer midday if the fee-product variant exists (else fail safe to evening-only).
      var middayUsable = tier !== 'not_available' && (tier === 'free' || s.variant);
      var windows = [WINDOWS.evening];
      if (middayUsable) windows.push(WINDOWS.midday);
      return {
        fsa: fsa, deliverable: true, kind: 'daily', zoneName: regionLabel(fsa),
        feeZone: bramptonSet[fsa] ? 'Z5' : 'Z1',
        days: null, windows: windows,
        middayTier: tier, surchargeCharge: middayUsable ? s.charge : 0, surchargeVariant: middayUsable ? s.variant : null,
      };
    }

    return { fsa: fsa, deliverable: false, kind: null, reason: 'out_of_area' };
  }

  /**
   * §8.1 base-fee preview for a zone at a given cart subtotal (cents).
   * Returns { belowMin, minOrder, fee, free, freeAt } — fee/freeAt in cents.
   * NOTE: preview only; the authoritative fee is charged by the native shipping app.
   */
  function computeBaseFee(feeZone, subtotalCents) {
    var z = FEE_ZONES[feeZone];
    if (!z) return null;
    var sub = subtotalCents || 0;
    if (sub < z.minOrder) return { belowMin: true, minOrder: z.minOrder, fee: null, free: false, freeAt: z.freeAt };
    if (sub >= z.freeAt) return { belowMin: false, minOrder: z.minOrder, fee: 0, free: true, freeAt: z.freeAt };
    for (var i = 0; i < z.tiers.length; i++) {
      if (sub < z.tiers[i].under) return { belowMin: false, minOrder: z.minOrder, fee: z.tiers[i].fee, free: false, freeAt: z.freeAt };
    }
    return { belowMin: false, minOrder: z.minOrder, fee: 0, free: true, freeAt: z.freeAt };
  }

  // ── canonical customer-facing fee phrase (ONE source for every checker/selector) ────────────
  // Owner 2026-07-12: all checkers/selectors read prices from this single source so a change here
  // propagates everywhere — never hardcode a per-zone price in a component again.
  // PRE-TAX (excl. HST), matching the canonical doc + the /pages/cake-delivery-toronto info page
  // ("fees before HST, added at checkout"), e.g. "from $4.95 (free over $85)". The base fee is
  // stored HST-inclusive in FEE_ZONES (what the cart charges), so divide by 1.13 for the pre-tax
  // display. freeAt is an ORDER-VALUE threshold (never taxed) → shown as whole dollars.
  var HST = 1.13;
  function money(cents) { return '$' + (cents / 100).toFixed(2); }
  function moneyPreTax(cents) { return money(Math.round(cents / HST)); }
  function wholeDollars(cents) { return '$' + Math.round(cents / 100); }
  function feeSummary(feeZone) {
    var f = computeBaseFee(feeZone, 5000);
    if (!f) return '';
    var s = (f.fee != null && f.fee > 0) ? 'from ' + moneyPreTax(f.fee) : 'delivery';
    if (f.freeAt) s += ' (free over ' + wholeDollars(f.freeAt) + ')';
    return s;
  }
  // pre-tax fee for a specific subtotal (for richer per-tier checker displays) — same single source.
  function feePreTax(feeZone, subtotalCents) {
    var f = computeBaseFee(feeZone, subtotalCents == null ? 5000 : subtotalCents);
    if (!f) return null;
    return { belowMin: f.belowMin, minOrder: f.minOrder, fee: f.fee == null ? null : Math.round(f.fee / HST),
             free: f.free, freeAt: f.freeAt };
  }

  // Pages print windows through snippets/tp-delivery-time.liquid as <span data-tp-time="evening">…</span>.
  // Rewrite them from this table on load, so a page can never show a window the table does not have.
  function fillTimes(root) {
    try {
      var els = (root || document).querySelectorAll('[data-tp-time]');
      for (var i = 0; i < els.length; i++) {
        var w = WINDOWS[els[i].getAttribute('data-tp-time')];
        if (w && els[i].textContent !== w.label) els[i].textContent = w.label;
      }
    } catch (e) {}
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { fillTimes(); });
  else fillTimes();

  window.TPDeliveryData = {
    fillTimes: fillTimes,
    normalizePostalCode: normalizePostalCode,
    lookupFSA: lookupFSA,
    computeBaseFee: computeBaseFee,
    feeSummary: feeSummary,
    feePreTax: feePreTax,
    middayTierOf: middayTierOf,
    FEE_PRODUCT_VARIANTS: FEE_PRODUCT_VARIANTS,
    WINDOWS: WINDOWS,
    normalizeWindowLabel: normalizeWindowLabel,
    PICKUP_WINDOWS: PICKUP_WINDOWS,
    PICKUP_SAMEDAY: PICKUP_SAMEDAY,
    NO_SAMEDAY_DOW: NO_SAMEDAY_DOW,
    CUTOFFS: CUTOFFS,
    FEE_ZONES: FEE_ZONES,
  };
})();
